import { useCallback, useRef, useState } from 'react';
import { placeCaretAfterAtomic } from '../../../DocEditor/utils/atomicCaret';
import { formatVariableRef } from '../../../../utils/variableScope';
import {
  createZwspMarker,
  deleteCharsBeforeCaret,
  menuPositionFromMarker,
} from '../utils/caret';

/** "{{" trigger + VariablePicker insert for nested value chips. */
export const useNestedVarPicker = ({
  elRef,
  readOnly,
  resolveVariable,
}) => {
  const pickerOpenRef = useRef(false);
  const [picker, setPicker] = useState(null);

  const clearInsertMarker = useCallback(() => {
    const el = elRef.current;
    if (!el) return;
    el.querySelectorAll('[data-var-insert-mark]').forEach((n) => n.remove());
  }, [elRef]);

  /** Remove the "{{" immediately before the caret. */
  const deleteTriggerBraces = useCallback(() => {
    if (!elRef.current) return false;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return false;
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) return false;
    const offset = range.startOffset;
    const text = node.textContent || '';
    if (offset < 2 || text.slice(offset - 2, offset) !== '{{') return false;
    return deleteCharsBeforeCaret(2);
  }, [elRef]);

  const openPickerAtCaret = useCallback(() => {
    const el = elRef.current;
    if (!el || readOnly || pickerOpenRef.current) return;
    // Lock immediately so input + keydown can't double-open
    pickerOpenRef.current = true;

    deleteTriggerBraces();
    clearInsertMarker();

    const marker = createZwspMarker('varInsertMark');
    const sel = window.getSelection();
    try {
      if (sel && sel.rangeCount > 0 && el.contains(sel.anchorNode)) {
        const range = sel.getRangeAt(0);
        range.collapse(true);
        range.insertNode(marker);
      } else {
        el.appendChild(marker);
      }
      if (sel) {
        const after = document.createRange();
        after.setStartAfter(marker);
        after.collapse(true);
        sel.removeAllRanges();
        sel.addRange(after);
      }
    } catch {
      clearInsertMarker();
      el.appendChild(marker);
    }

    const { x, y } = menuPositionFromMarker(marker, el);
    setPicker({ x, y });
  }, [clearInsertMarker, deleteTriggerBraces, elRef, readOnly]);

  const insertNestedRef = useCallback(
    (name, index = null) => {
      const el = elRef.current;
      if (!el || readOnly) return;
      el.focus();

      const ref = formatVariableRef(name, index) || name;
      const hit = resolveVariable?.(ref) || null;
      const color = hit?.color || 'rgba(236, 240, 245, 0.75)';
      const display =
        hit && String(hit.value ?? '').trim() !== ''
          ? String(hit.value)
          : `{{${ref}}}`;
      const span = document.createElement('span');
      span.className = 'variables-panel__nested-var';
      span.dataset.varRef = ref;
      span.contentEditable = 'false';
      span.style.color = color;
      span.title = hit
        ? `${ref}${hit.sourceName ? ` — ${hit.sourceName}` : ''}`
        : `${ref} — unresolved`;
      span.textContent = display;

      const marker = el.querySelector('[data-var-insert-mark]');
      if (marker) {
        marker.parentNode.insertBefore(span, marker);
        marker.remove();
      } else {
        el.appendChild(span);
      }

      const placeCaretAfter = () => {
        el.focus();
        if (!el.contains(span)) return;
        placeCaretAfterAtomic(span);
      };

      placeCaretAfter();
      pickerOpenRef.current = false;
      setPicker(null);
      // Keep edits local until the cell blurs — avoids resolving the whole
      // doc on every nested-var insert keystroke.
      requestAnimationFrame(placeCaretAfter);
    },
    [elRef, readOnly, resolveVariable]
  );

  const closePicker = useCallback(() => {
    clearInsertMarker();
    pickerOpenRef.current = false;
    setPicker(null);
    elRef.current?.focus();
  }, [clearInsertMarker, elRef]);

  return {
    picker,
    pickerOpenRef,
    openPickerAtCaret,
    insertNestedRef,
    closePicker,
  };
};
